import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import type { Document, Manifest } from '../../api/authoring'
import { editorFor } from '../../editors'
import type { CommandTarget, SelectedNode } from '../../model/command'
import { typeTitle, uploadsInto } from '../../model/manifest'
import { BlobUploader } from '../../ui/BlobUploader'
import { ReferenceEditor } from '../../ui/ReferenceEditor'

/**
 * One block's editor, opened where the block is.
 *
 * **It sits in the tree rather than beside it**, under the row it
 * belongs to, and that is the whole point of the arrangement. The panel
 * this replaced shared the second column with the preview frame, so
 * selecting a block took the page away at the moment an author most
 * wants to see it — the frame exists for confidence and selection spent
 * it. Editing in place gives the column back to the frame permanently:
 * the words on the left, the page on the right, at the same time.
 *
 * **The row indents, the editor does not.** A block three deep is drawn
 * three deep, and an editor drawn to match would hand a rich-text
 * control a column narrowed by the accident of where the block sits.
 * Depth is a fact about the tree, not about the form over one node.
 *
 * What it draws is what the panel drew, unchanged and in the same order:
 * the picture where the type holds one, what the block refers to, and
 * the type's own editor. The screen resolves *where the intents go*
 * before any of it — see `model/command.ts` — so nothing here works out
 * an address for itself.
 */
export function BlockEditor({
  tenant,
  manifest,
  node,
  target,
  document,
  busy,
  send,
  onClose,
}: {
  tenant: string
  manifest: Manifest | undefined
  /** The selected node, as it stands. */
  node: SelectedNode
  /** Where an intent editing this node's own fields is addressed. */
  target: CommandTarget
  /** The document those fields live in, for what it already holds. */
  document: Document | undefined
  busy: boolean
  send: (intent: string, payload: Record<string, unknown>) => Promise<unknown>
  onClose: () => void
}) {
  const Editor = editorFor(node.type, manifest)

  return (
    <Box
      sx={{
        px: 2,
        py: 2,
        bgcolor: 'background.default',
        borderTop: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h3" sx={{ flexGrow: 1 }}>
            {typeTitle(manifest, node.type)}
          </Typography>
          <Button size="small" startIcon={<CloseIcon />} onClick={onClose}>
            Done
          </Button>
        </Stack>

        {/* The picture, where the block being edited is one. "There is
            no photo here" and "I want to upload one" are one moment, and
            until this control moved into the editor the author had to go
            and find a screen. It is offered by asking the manifest
            whether this type holds a file, never by knowing which type
            does — and only where the command addresses a whole
            component, because an upload is scoped to one and an inline
            node is not one. */}
        {target.path.length === 0 && uploadsInto(manifest, node.type, node.fields) && (
          <BlobUploader tenant={tenant} id={target.id} document={document} disabled={busy} />
        )}

        {/* References are edited in the same place as fields. A
            repeating block needs both a declaration and a source
            collection before it can expand, and a pattern needs its
            declaration and input for the same reason. */}
        <ReferenceEditor
          tenant={tenant}
          manifest={manifest}
          typeName={node.type}
          references={node.references}
          fields={node.fields}
          commandId={target.id}
          commandPath={target.path}
          send={send}
          busy={busy}
        />

        <Editor
          tenant={tenant}
          commandId={target.id}
          commandPath={target.path}
          fields={node.fields}
          children={node.children}
          references={node.references}
          manifest={manifest}
          typeName={node.type}
          register="content"
          busy={busy}
          send={send}
        />
      </Stack>
    </Box>
  )
}
